import React, { useMemo, useState } from 'react';
import { Message, SelectPicker, toaster } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useHasConnectSuite,
  useHasPursuitConnect,
  useProjectPursuitLink,
  useProjectPursuitLinkMutation,
  usePursuitLinkCandidates,
} from '../hooks/useQueries';
import './PursuitLink.css';

// The PursuitConnect pursuit a project is linked to, as a field in the project details. Linked, it
// opens the pursuit in PursuitConnect; otherwise anyone with Manage Project can link one. Both apps
// keep the link, and ProPlan's server changes both (docs/pursuitconnect-link.md).

const PURSUIT_CONNECT_LOGO = '/res/pc_logo_192.png';

const notify = (type, text) => {
  toaster.push(
    <Message type={type} showIcon closable>
      {text}
    </Message>,
    { placement: 'topEnd', duration: type === 'error' ? 6000 : 2500 }
  );
};

// Searchable list of the pursuits this person can see in PursuitConnect that aren't linked yet.
function PursuitLinkPicker({ open, onClose, projectId, onLink, linking }) {
  const [value, setValue] = useState(null);
  const candidates = usePursuitLinkCandidates(projectId, { enabled: open });

  const data = useMemo(
    () =>
      (Array.isArray(candidates.data) ? candidates.data : []).map((p) => ({
        value: p.id,
        label: p.name,
        meta: [p.stage, p.businessSector, p.owner].filter(Boolean).join(' · '),
      })),
    [candidates.data]
  );

  const close = () => {
    if (linking) return;
    onClose();
  };

  let body;
  if (candidates.isLoading) {
    body = <p className="ppc-pl-note">Loading from PursuitConnect…</p>;
  } else if (candidates.error) {
    body = (
      <Message type="error" showIcon>
        {String(candidates.error?.message || 'Could not load pursuits from PursuitConnect.')}
      </Message>
    );
  } else if (data.length === 0) {
    body = (
      <Message type="info" showIcon>
        There are no pursuits to link. Every pursuit you can see in PursuitConnect is already linked to a project.
      </Message>
    );
  } else {
    body = (
      <SelectPicker
        block
        searchable
        data={data}
        value={value}
        onChange={setValue}
        placeholder="Search pursuits"
        disabled={linking}
        menuStyle={{ zIndex: 1100 }}
        renderMenuItem={(label, item) => (
          <div className="ppc-pl-option">
            <span className="ppc-pl-option__name">{label}</span>
            {item.meta ? <span className="ppc-pl-option__meta">{item.meta}</span> : null}
          </div>
        )}
      />
    );
  }

  return (
    <Modal open={open} onClose={close} onExited={() => setValue(null)} size="sm">
      <Modal.Header>
        <Modal.Title>Link a PursuitConnect pursuit</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ppc-pl-picker">
          <p className="ppc-pl-note">
            Pick the pursuit this project came from. Only pursuits you can see in PursuitConnect are listed.
          </p>
          {body}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NsiButton onClick={close} disabled={linking}>
          Cancel
        </NsiButton>
        <NsiButton variant="primary" disabled={!value} loading={linking} onClick={() => onLink(value)}>
          Link
        </NsiButton>
      </Modal.Footer>
    </Modal>
  );
}

// The "PursuitConnect pursuit" field. canEdit false (no Manage Project): the link shows, the
// link and unlink buttons don't. Hidden when PursuitConnect isn't connected on this server.
export function ProjectPursuitLinkField({ projectId, canEdit = false }) {
  const hasConnectSuite = useHasConnectSuite();
  const hasPursuitConnect = useHasPursuitConnect();
  const enabled = hasConnectSuite && hasPursuitConnect;
  const link = useProjectPursuitLink(projectId, { enabled });
  const mutation = useProjectPursuitLinkMutation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmUnlinkOpen, setConfirmUnlinkOpen] = useState(false);

  if (!enabled || link.data?.available === false) return null;

  const pursuit = link.data?.pursuit ?? null;

  const run = (pursuitId, successText, after) => {
    mutation.mutate(
      { projectId, pursuitId },
      {
        onSuccess: () => {
          notify('success', successText);
          after?.();
        },
        onError: (err) => notify('error', String(err?.message || 'PursuitConnect request failed.')),
      }
    );
  };

  let value;
  if (link.isLoading) {
    value = <span className="ppc-pd-hero__stat ppc-pl-muted">Loading…</span>;
  } else if (link.error) {
    value = (
      <button type="button" className="ppc-pl-retry" onClick={() => link.refetch()} title={String(link.error?.message || '')}>
        Couldn&apos;t load · Try again
      </button>
    );
  } else if (pursuit) {
    value = (
      <span className="ppc-pl-linked">
        <a className="ppc-pl-chip" href={pursuit.url} title={`Open "${pursuit.name}" in PursuitConnect`}>
          <img className="ppc-pl-logo" src={PURSUIT_CONNECT_LOGO} alt="" />
          <span className="ppc-pl-chip__name">{pursuit.name}</span>
          <i className="bi bi-box-arrow-up-right ppc-pl-chip__go" aria-hidden="true" />
        </a>
        {canEdit ? (
          <button
            type="button"
            className="ppc-pl-iconbtn"
            title="Unlink this pursuit"
            aria-label="Unlink this pursuit"
            onClick={() => setConfirmUnlinkOpen(true)}
            disabled={mutation.isPending}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        ) : null}
      </span>
    );
  } else if (canEdit) {
    value = (
      <button type="button" className="ppc-pl-add" onClick={() => setPickerOpen(true)} disabled={mutation.isPending}>
        <img className="ppc-pl-logo" src={PURSUIT_CONNECT_LOGO} alt="" />
        Link a pursuit
      </button>
    );
  } else {
    value = <span className="ppc-pd-hero__stat">—</span>;
  }

  return (
    <div className="ppc-pl-field">
      <span className="ppc-pd-label">PursuitConnect pursuit</span>
      {value}

      {canEdit ? (
        <>
          <PursuitLinkPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            projectId={projectId}
            linking={mutation.isPending}
            onLink={(pursuitId) => run(pursuitId, 'Linked to the pursuit.', () => setPickerOpen(false))}
          />

          <Modal open={confirmUnlinkOpen} onClose={() => !mutation.isPending && setConfirmUnlinkOpen(false)} size="xs">
            <Modal.Header>
              <Modal.Title>Unlink the pursuit?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="ppc-pl-note">
                This project will no longer be linked to <strong>{pursuit?.name}</strong>, here or in PursuitConnect.
                Nothing is deleted in either app.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <NsiButton onClick={() => setConfirmUnlinkOpen(false)} disabled={mutation.isPending}>
                Cancel
              </NsiButton>
              <NsiButton
                variant="danger-solid"
                loading={mutation.isPending}
                onClick={() => run(null, 'Unlinked from the pursuit.', () => setConfirmUnlinkOpen(false))}
              >
                Unlink
              </NsiButton>
            </Modal.Footer>
          </Modal>
        </>
      ) : null}
    </div>
  );
}
